import type { ClassValue } from 'clsx';

import {
  GlossaryIndex,
  GlossaryIndexColumns,
  GlossaryIndexCount,
  GlossaryIndexGroup,
  GlossaryIndexGroupLetter,
  GlossaryIndexGroupTerms,
  GlossaryIndexTerm,
} from '@vs-shop/commerce-ui/glossaryIndex';
import { clsxm } from '@vs-shop/utils';

import { getMemoizedIntlShapeWithDefaultLocale } from '../../../../utils/intl';

import { GlossarySearchHint } from '../../../client/page/glossary/GlossarySearchHint';

import { groupTermsByFirstLetter } from '../../../../effect/data/glossary/groupTermsByFirstLetter';
import type { GlossaryTermIndex } from '@vs-shop/schemas/branded/MiscSchema';

type Props = {
  terms: readonly GlossaryTermIndex[];
  className?: ClassValue;
};

export function GlossaryGroupedList({ terms, className }: Props) {
  const intl = getMemoizedIntlShapeWithDefaultLocale();
  const groups = groupTermsByFirstLetter(terms);

  return (
    <GlossaryIndex data-mode="search" className={clsxm(className)}>
      <GlossaryIndexCount className="mb-4">
        {intl.formatMessage(
          {
            id: 'component.client.page.glossary.searchResults.count',
            defaultMessage:
              '{count, plural, one {Nalezen # pojem} few {Nalezeny # pojmy} other {Nalezeno # pojmů}}',
          },
          { count: terms.length }
        )}
      </GlossaryIndexCount>

      <GlossarySearchHint className="mb-8 tab:mb-12" />

      <GlossaryIndexColumns>
        {groups.map(([letter, letterTerms]) => (
          <GlossaryIndexGroup key={letter}>
            <GlossaryIndexGroupLetter>{letter}</GlossaryIndexGroupLetter>
            <GlossaryIndexGroupTerms>
              {letterTerms.map((term) => (
                // A cell inside a group carries nothing: the group is already
                // unbreakable and its list sets the rhythm, so there is no part
                // here to compose.
                <li key={term.code}>
                  <GlossaryIndexTerm href={term.routerUrl}>{term.name ?? ''}</GlossaryIndexTerm>
                </li>
              ))}
            </GlossaryIndexGroupTerms>
          </GlossaryIndexGroup>
        ))}
      </GlossaryIndexColumns>
    </GlossaryIndex>
  );
}
